


import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Grid,
  Divider,
  Chip,
  Avatar,
  Alert,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  Person,
  Biotech,
  LocalShipping,
  Receipt,
  Home,
  ThermostatAuto
} from '@mui/icons-material';

const CompletionScreen = ({ order, onBackToOrders }) => {
  // ── helpers ─────────────────────────────────────────────────────────────
  const fmt = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // treats "" the same as null/undefined
  const val = (a, b) => {
    const v = a ?? b;
    return (typeof v === 'string' && v.trim() === '') ? null : v;
  };

  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : null;

  const getStorageIcon = (c) => {
    if (!c) return '📦';
    const lo = c.toLowerCase();
    if (lo.includes('refrigerated')) return '❄️';
    if (lo.includes('frozen'))       return '🧊';
    if (lo.includes('room'))         return '🌡️';
    return '📦';
  };

  const qualityColor = (q) => {
    switch ((q || '').toLowerCase()) {
      case 'excellent': case 'good':      return 'success';
      case 'acceptable':                  return 'info';
      case 'hemolyzed': case 'lipemic':   return 'warning';
      case 'poor': case 'clotted':
      case 'insufficient': case 'contaminated': return 'error';
      default: return 'default';
    }
  };

  const statusColor = { collected: 'info', transferred: 'warning', received_at_lab: 'success' };

  const samples      = order.sampleCollections || [];
  const labTransfers = order.labTransfers      || [];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>

      {/* ── Success Header ──────────────────────────────────────────────── */}
      <Card sx={{ mb: 3, bgcolor: '#d4edda', border: '2px solid #28a745' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: '#28a745', mx: 'auto', mb: 2 }}>
            <CheckCircle sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight={700} color="#155724" gutterBottom>
            Collection Completed!
          </Typography>
          <Typography variant="body1" color="#155724">
            Sample collection has been successfully completed and transferred to the lab.
          </Typography>
        </CardContent>
      </Card>

      {/* ── Order Summary ───────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Order Summary</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Receipt fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Order ID</Typography>
              </Stack>
              <Typography variant="body1" fontWeight={600}>#{order.orderId}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Person fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Patient</Typography>
              </Stack>
              <Typography variant="body1" fontWeight={600}>{order.customerName}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Biotech fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Test / Service</Typography>
              </Stack>
              <Typography variant="body1" fontWeight={600}>{order.serviceName}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Receipt fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">Amount</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700} color="#006D77">₹{order.totalAmount}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Collected Samples ───────────────────────────────────────────── */}
      {samples.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Biotech sx={{ color: '#006D77' }} />
              <Typography variant="h6" fontWeight={600}>Collected Samples</Typography>
              <Chip label={`${samples.length} sample${samples.length !== 1 ? 's' : ''}`} size="small" color="info" />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {samples.map((sample, idx) => {
                // resolve every field — snake_case or camelCase, "" → null
                const testName         = val(sample.test_name,         sample.testName)         || `Sample ${idx + 1}`;
                const testPrice        = sample.test_price             ?? sample.testPrice;
                const sampleType       = val(sample.sample_type,       sample.sampleType);
                const sampleQuality    = val(sample.sample_quality,    sample.sampleQuality)    || 'good';
                const vialNumber       = val(sample.vial_number,       sample.vialNumber);
                const collectionTime   = val(sample.collection_time,   sample.collectionTime);
                const patientCondition = val(sample.patient_condition, sample.patientCondition);
                const fastingStatus    = val(sample.fasting_status,    sample.fastingStatus);
                const specialNotes     = val(sample.special_notes,     sample.specialNotes);
                const temperature      = val(sample.temperature,       sample.temperature);
                const storageCondition = val(sample.storage_condition, sample.storageCondition);
                const collectedBy      = val(sample.collected_by,      sample.collectedBy);
                const seqNum           = sample.sequence_number        ?? sample.sequenceNumber  ?? null;
                const status           = val(sample.status,            sample.status);
                const createdAt        = val(sample.created_at,        sample.createdAt);

                return (
                  <Card key={idx} variant="outlined">
                    <CardContent>
                      {/* header row: #seq · name · quality · status */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {seqNum !== null && (
                            <Chip label={`#${seqNum}`} size="small" variant="outlined"
                              sx={{ borderColor: '#006D77', color: '#006D77', fontWeight: 600 }} />
                          )}
                          <Typography variant="subtitle2" fontWeight={600}>{testName}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75}>
                          <Chip label={sampleQuality.toUpperCase()} size="small" color={qualityColor(sampleQuality)} />
                          {status && (
                            <Chip label={cap(status)} size="small" color={statusColor[status] || 'default'} variant="outlined" />
                          )}
                        </Stack>
                      </Stack>

                      {/* details grid */}
                      <Grid container spacing={1.5}>
                        {testPrice !== null && testPrice !== undefined && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Test Price</Typography>
                              <Typography variant="body2" fontWeight={600} color="#006D77">₹{Number(testPrice).toFixed(2)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {sampleType && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Sample Type</Typography>
                              <Typography variant="body2" fontWeight={500}>{sampleType}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {vialNumber && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Vial Number</Typography>
                              <Typography variant="body2" fontWeight={500}>{vialNumber}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {storageCondition && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Storage</Typography>
                              <Typography variant="body2" fontWeight={500}>{getStorageIcon(storageCondition)} {cap(storageCondition)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {patientCondition && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Patient Condition</Typography>
                              <Typography variant="body2" fontWeight={500}>{cap(patientCondition)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {fastingStatus && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Fasting Status</Typography>
                              <Typography variant="body2" fontWeight={500}>{cap(fastingStatus)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {temperature && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Temperature</Typography>
                              <Typography variant="body2" fontWeight={500}>{temperature}°C</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {collectedBy && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Collected By</Typography>
                              <Typography variant="body2" fontWeight={500}>{collectedBy}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {collectionTime && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Collection Time</Typography>
                              <Typography variant="body2" fontWeight={500}>{fmt(collectionTime)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {/* created_at only if meaningfully different from collection_time */}
                        {createdAt && collectionTime &&
                          new Date(createdAt).toISOString().slice(0,16) !== new Date(collectionTime).toISOString().slice(0,16) && (
                          <Grid item xs={6} sm={4}>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                              <Typography variant="caption" color="text.secondary">Record Created</Typography>
                              <Typography variant="body2" fontWeight={500}>{fmt(createdAt)}</Typography>
                            </Paper>
                          </Grid>
                        )}

                        {specialNotes && (
                          <Grid item xs={12}>
                            <Alert severity="info" sx={{ py: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Special Notes</Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{specialNotes}</Typography>
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Lab Transfer Details ────────────────────────────────────────── */}
      {labTransfers.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <LocalShipping sx={{ color: '#006D77' }} />
              <Typography variant="h6" fontWeight={600}>Lab Transfer Details</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {labTransfers.map((lt, idx) => {
              // resolve snake_case from backend
              const labName                = val(lt.lab_name,                lt.labName);
              const labAddress             = val(lt.lab_address,             lt.labAddress);
              const labContact             = val(lt.lab_contact,             lt.labContact);
              const transportMode          = val(lt.transport_mode,          lt.transportMode);
              const courierService         = val(lt.courier_service,         lt.courierService);
              const trackingNumber         = val(lt.tracking_number,         lt.trackingNumber);
              const estimatedDelivery      = val(lt.estimated_delivery_time, lt.estimatedDeliveryTime);
              const temperatureControlled  = lt.temperature_controlled       ?? lt.temperatureControlled ?? null;
              const specialHandling        = val(lt.special_handling,        lt.specialHandling);
              const transferredAt          = val(lt.transferred_at,          lt.transferTime);
              const status                 = val(lt.status,                  lt.status);

              return (
                <Box key={idx}>
                  {idx > 0 && <Divider sx={{ my: 2 }} />}
                  <Grid container spacing={1.5}>

                    {labName && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f0fafa', borderColor: '#006D77' }}>
                          <Typography variant="caption" color="text.secondary">Lab Name</Typography>
                          <Typography variant="body1" fontWeight={600} color="#006D77">{labName}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {labAddress && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Lab Address</Typography>
                          <Typography variant="body2" fontWeight={500}>{labAddress}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {labContact && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Lab Contact</Typography>
                          <Typography variant="body2" fontWeight={500} component="a" href={`tel:${labContact}`} sx={{ color: '#006D77', textDecoration: 'none' }}>
                            {labContact}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {transportMode && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Transport Mode</Typography>
                          <Typography variant="body2" fontWeight={500}>{cap(transportMode)}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {courierService && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Courier Service</Typography>
                          <Typography variant="body2" fontWeight={500}>{courierService}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {trackingNumber && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Tracking Number</Typography>
                          <Typography variant="body2" fontWeight={600} color="#006D77">{trackingNumber}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {transferredAt && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Transferred At</Typography>
                          <Typography variant="body2" fontWeight={500}>{fmt(transferredAt)}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {estimatedDelivery && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Est. Delivery</Typography>
                          <Typography variant="body2" fontWeight={500}>{fmt(estimatedDelivery)}</Typography>
                        </Paper>
                      </Grid>
                    )}

                    {temperatureControlled !== null && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: temperatureControlled ? '#e8f5e9' : '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">
                            <Stack direction="row" alignItems="center" spacing={0.3}>
                              <ThermostatAuto fontSize="small" /> Temp Controlled
                            </Stack>
                          </Typography>
                          <Chip
                            label={temperatureControlled ? 'Yes' : 'No'}
                            size="small"
                            color={temperatureControlled ? 'success' : 'default'}
                          />
                        </Paper>
                      </Grid>
                    )}

                    {status && (
                      <Grid item xs={6} sm={4}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                          <Typography variant="caption" color="text.secondary">Transfer Status</Typography>
                          <Chip
                            label={cap(status)}
                            size="small"
                            color={status === 'received_at_lab' ? 'success' : status === 'in_transit' ? 'warning' : 'default'}
                          />
                        </Paper>
                      </Grid>
                    )}

                    {specialHandling && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Special Handling</Typography>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{specialHandling}</Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── Payment Status ──────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Payment Status</Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" alignItems="center" spacing={2}>
            <CheckCircle color={order.isPaid ? 'success' : 'disabled'} sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="body1" fontWeight={600} color={order.isPaid ? 'success.main' : 'text.secondary'}>
                {order.isPaid ? 'Payment Completed' : 'Payment Pending'}
              </Typography>
              {order.paymentMethod && (
                <Typography variant="body2" color="text.secondary">
                  Method: {order.paymentMethod.toUpperCase()}
                </Typography>
              )}
              {order.paymentCollectedAt && (
                <Typography variant="caption" color="text.secondary">
                  Collected at: {fmt(order.paymentCollectedAt)}
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Final Status Badge ──────────────────────────────────────────── */}
      <Card sx={{ mb: 3, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <CheckCircle color="success" />
            <Chip
              label={order.orderStatus?.replace(/_/g, ' ').toUpperCase() || 'COMPLETED'}
              color="success"
              sx={{ fontSize: '0.95rem', py: 2.5, px: 2 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* ── Back button ─────────────────────────────────────────────────── */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onBackToOrders}
        startIcon={<Home />}
        sx={{ py: 1.5, bgcolor: '#006D77', '&:hover': { bgcolor: '#005561' } }}
      >
        BACK TO ORDERS
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        Thank you for your service. The samples are now with the lab for processing.
      </Typography>
    </Box>
  );
};

export default CompletionScreen;